<template>
  <div class="statistic">
    <div class="statistic-row">
      <div class="label">{{ trans("live.tabs.statistic.current_duration") }}</div>
      <div class="value">
        <timer
          :data-days="timerDays"
          :data-hours="timerHours"
          :data-minutes="timerMinutes"
          :data-seconds="timerSeconds"
          :is-paused="timerIsPaused"
        ></timer>
      </div>
    </div>
    <div class="statistic-row">
      <div class="label">{{ trans("live.tabs.statistic.time_per_lot") }}</div>
      <div class="value">{{ timePerLot }} {{ trans("live.tabs.statistic.sec") }}</div>
    </div>
    <div class="statistic-row">
      <div class="label">{{ trans("live.tabs.statistic.lots_in_auction") }}</div>
      <div class="value">
        {{ lots.length }}
      </div>
    </div>
    <div class="statistic-row">
      <div class="label">{{ trans("live.tabs.statistic.sold_unsold") }}</div>
      <div class="value">{{ soldLots.length }} / {{ unsoldLots.length }}</div>
    </div>
    <div class="statistic-row">
      <div class="label">{{ trans("live.tabs.statistic.total_sales") }}</div>
      <div class="value">
        {{ format_currency(totalSales) }}
      </div>
    </div>
    <div class="statistic-row">
      <div class="label">{{ trans("live.tabs.statistic.highest_price") }}</div>
      <div class="value">
        {{ format_currency(highestPrice) }}
      </div>
    </div>
    <div class="statistic-row">
      <div class="label">{{ trans("live.tabs.statistic.bidders_participated") }}</div>
      <div class="value">
        {{ biddersCount }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Timer from "@/components/_partials/Timer.vue";
  import moment from "moment-timezone";
  import { computed, ref, watch } from "vue";
  import { trans } from "@/utils/trans";
  import { format_currency } from "@/utils/currencies";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { ILot } from "@/models";
  import { LotStatuses, LotSoldStatuses, AuctionStatuses } from "@/enum";

  const auctionStore = useAuctionStore();
  const auction = computed(() => auctionStore.auction);
  const auctionStatus = computed(() => auctionStore.auction.status);
  const biddersCount = computed(() => auction.value.bidders_count);
  const lots = computed(() => auctionStore.lots);
  const passedLots = computed(() => lots.value.filter(
      (item) => item.status === LotStatuses.LIVE || item.status === LotStatuses.ENDED)
  );
  const soldLots = computed(() => {
    return auctionStore.lots?.filter((lot: ILot) => lot.sold_status === LotSoldStatuses.SOLD);
  });
  const unsoldLots = computed(() => {
    return auctionStore.lots?.filter((lot: ILot) => lot.sold_status === LotSoldStatuses.UNSOLD);
  });
  const totalSales = computed(() => soldLots.value.reduce((sum, lot) => sum + lot.current_bid, 0));
  const highestPrice = computed(() => soldLots.value.reduce((max, lot) => Math.max(max, lot.current_bid), 0));

  const currentDuration = ref(0);

  const timerDays = computed(() => Math.floor(currentDuration.value / (24 * 60 * 60)));
  const timerHours = computed(() => Math.floor((currentDuration.value % (24 * 60 * 60)) / (60 * 60)));
  const timerMinutes = computed(() => Math.floor((currentDuration.value % (60 * 60)) / 60));
  const timerSeconds = computed(() => currentDuration.value % 60);
  const timerIsPaused = computed(() => auctionStatus.value === AuctionStatuses.ENDED);

  const timePerLot = computed(() => passedLots.value.length
    ? Math.floor(currentDuration.value / passedLots.value.length)
    : 0
  );

  const updateCurrentDuration = () => {
    const timerStart = auction.value.live_auction_start ? moment(auction.value.live_auction_start) : null;
    const timerEnd = auctionStatus.value === AuctionStatuses.ENDED && auction.value.live_auction_end
        ? moment(auction.value.live_auction_end)
        : moment();

    if (!timerStart) return 0;

    const diffInSeconds = timerEnd.diff(timerStart, 'seconds');
    currentDuration.value = diffInSeconds > 0 ? diffInSeconds : 0;
  };

  updateCurrentDuration();

  watch(passedLots, () => {
    updateCurrentDuration();
  });

</script>
